import { createContext, useState, useCallback } from "react";
import axiosInstance from "../services/axiosConfig";
import BaseEnum from "enums/BaseEnum";
import { formatStatusText } from "utils/statusFormatter";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export const SalesContext = createContext();

// eslint-disable-next-line react/prop-types
export const SalesProvider = ({ children }) => {
  const [salesData, setSalesData] = useState({ rows: [], columns: [] });
  const formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString("tr-TR");
  };

  const handleUnauthorized = useCallback(() => {
    toast.warn(`Bu içeriği görüntüleme yetkiniz yok.`, { position: "top-right" });
  }, []);

  const roleEndpoints = {
    ROLE_SALES_RESPONSIBLE: ["sales/my-requests"],
    ROLE_LOGISTICS_OFFICER: ["logistics/requests"],
    ROLE_ACCOUNTANT: ["accountant/invoice/pending-orders", "accountant/invoice/pending-logistics"],
    ROLE_MANAGER: ["manager/requests"],
    ROLE_RESOURCE: ["resource/request/pending-approvals"],
    ROLE_CUSTOMER: ["sales/customer/requests"],
  };
  const defaultEndpoints = ["manager/requests"];
  const requestDetailEndpoints = {
    ROLE_RESOURCE: "resource/request",
    ROLE_LOGISTICS_OFFICER: "logistics/request",
    ROLE_MANAGER: "manager/request",
  };

  const fetchSalesData = useCallback(
    async (role) => {
      const endpoints = roleEndpoints[role] || defaultEndpoints;

      try {
        const allData = await Promise.all(
          endpoints.map(async (endpoint) => {
            const response = await axiosInstance.get(endpoint);
            return response.data;
          })
        );

        const isCustomerRole = role === "ROLE_CUSTOMER";

        const rows = allData.flat().map((item) => {
          const statusValue = isCustomerRole ? item.customerStage || item.status : item.status;
          const logisticsStatusValue = isCustomerRole
            ? undefined
            : formatStatusText(item.logisticsStatus);

          return {
            id: item.id ?? item.requestId ?? item.orderId,
            customerName: item.customer?.name || "N/A",
            price: item.price,
            amount: item.amount,
            materialType: item.materialType,
            paymentType: item.paymentType,
            term: item.term,
            size: item.size,
            classification: item.classification,
            packaging: item.packaging,
            requestedDeliveryDate: formatDate(item.requestedDeliveryDate),
            comment: item.comment,
            status: statusValue,
            statusCode: item.statusCode,
            createdById: item.createdById,
            createdByName: item.getCreatedByName,
            pricePerTon: item.pricePerTon,
            logisticsStatus: logisticsStatusValue,
            logisticsComment: isCustomerRole ? undefined : item.logisticsComment,
            logisticsPrice: isCustomerRole ? undefined : item.logisticsPrice,
            isConvertedToOrder: item.isConvertedToOrder,
            customerStage: item.customerStage,
            fullData: item,
          };
        });

        const columns = [
          { field: "id", headerName: "ID", width: 90 },
          { field: "customerName", headerName: BaseEnum.NAME, width: 150 },
          { field: "price", headerName: BaseEnum.PRICE, width: 150 },
          { field: "amount", headerName: BaseEnum.AMOUNT, width: 150 },
          { field: "materialType", headerName: BaseEnum.MATERIAL_TYPE, width: 150 },
          { field: "paymentType", headerName: BaseEnum.PAYMENT_TYPE, width: 150 },
          { field: "term", headerName: BaseEnum.TERM, width: 150 },
          { field: "size", headerName: BaseEnum.SIZE, width: 150 },
          { field: "classification", headerName: BaseEnum.CLASSIFICATION, width: 150 },
          { field: "packaging", headerName: BaseEnum.PACKAGING, width: 150 },
          {
            field: "requestedDeliveryDate",
            headerName: BaseEnum.REQUESTED_DELIVERY_DATE,
            width: 150,
          },
          ...(!isCustomerRole
            ? [{ field: "comment", headerName: BaseEnum.COMMENT, width: 150 }]
            : []),
          { field: "status", headerName: BaseEnum.STATUS, width: 150 },
          { field: "createdByName", headerName: BaseEnum.CREATED_BY, width: 150 },
          { field: "pricePerTon", headerName: BaseEnum.PRICE_PER_TON, width: 150 },
          ...(!isCustomerRole
            ? [
                { field: "logisticsStatus", headerName: BaseEnum.LOGISTICS_STATUS, width: 150 },
                { field: "logisticsComment", headerName: BaseEnum.LOGISTICS_COMMENT, width: 150 },
                { field: "logisticsPrice", headerName: BaseEnum.LOGISTICS_PRICE, width: 150 },
              ]
            : []),
        ];

        const filteredRows =
          role === "ROLE_CUSTOMER"
            ? rows.filter(
                (row) =>
                  !row.isConvertedToOrder &&
                  row.statusCode !== "SIPARIS_OLUSTURULDU" &&
                  row.statusCode !== "IPTAL_EDILDI"
              )
            : rows;

        setSalesData({ rows: filteredRows, columns });
        return { rows: filteredRows, columns };
      } catch (error) {
        if (error.response?.status === 403) handleUnauthorized();
        console.error(`Failed to fetch data for role ${role}:`, error);
        return { rows: [], columns: [] };
      }
    },
    [handleUnauthorized]
  );

  const getById = useCallback(
    async (id, role) => {
      try {
        const baseEndpoint = requestDetailEndpoints[role] || "sales/get-request";
        const response = await axiosInstance.get(`${baseEndpoint}/${id}`);
        return response.data;
      } catch (error) {
        if (error.response?.status === 403) handleUnauthorized();
        console.error(`Failed to fetch data for ID ${id}:`, error);
        return null;
      }
    },
    [handleUnauthorized]
  );

  return (
    <SalesContext.Provider value={{ ...salesData, fetchSalesData, getById }}>
      {children}
    </SalesContext.Provider>
  );
};
